import {
  RadioButton,
  RadioButtonChangeEvent,
  RadioGroup,
  RadioGroupChangeEvent,
} from '@progress/kendo-react-inputs';
import { isEmpty, sumBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useAuth from 'src/hooks/useAuth';
import useConfirm from 'src/hooks/useConfirm';
import usePeriodView from 'src/hooks/usePeriodView';
import useRedirect from 'src/hooks/useRedirect';
import useAllGetFiscalYearsApi, {
  FiscalAllYearsResponse,
} from 'src/services/useAllGetFiscalYearsApi';
import useExportGLBudgetsApi from 'src/services/useExportGLBudgetsApi';
import {
  ManagementCompanyObject,
  ManagementCompanyResponse,
} from 'src/services/useGetManagementCompanyApi';
import { SetState } from 'src/types/generic';
import { errorIcon } from 'src/utils/icons';
import { formatFiscalYears, getUniqueProperties } from 'src/utils/transformation';
import FiscalYearDropdown from './FiscalYearDropdown';
import ManagementCompanyDropdown from './ManagementCompanyDropdown';
import PropertyDropdown from './PropertyDropdown';

export interface fiscalYearType {
  startDate: string;
  endDate: string;
  content?: string;
}

const fileTypeData = [
  { label: '.xls (Actual output will be .txt if rows exceed 65k)', value: 'xls' },
  { label: '.txt (tab delimited)', value: 'txt' },
];
const tabData = [
  { label: 'Multi-Tab File (one property per tab)', value: 'multi' },
  { label: 'Single Tab File (all properties in one tab)', value: 'single' },
];
const glCodeTypeData = [
  { label: 'Only Active GL Codes', value: 'active' },
  { label: 'All GL Codes', value: 'all' },
];
const amountTypeData = [
  { label: 'Include Budget Amounts', value: 'budget' },
  { label: 'Include Forecast Amounts', value: 'forecast' },
];

export type PropertyObject = FiscalAllYearsResponse;

//! change this after testing
export const MAX_EXCEL_ROWS = 65000;
// export const MAX_EXCEL_ROWS = 1000;

interface props {
  managementCompanies: ManagementCompanyResponse;
  isManagementCompanyLoading: boolean;
  fileDownloadLoading: boolean;
  setFileDownloadLoading: SetState<boolean>;
}

const Export = (props: props) => {
  const { isSuperAdmin } = useAuth();
  const { t, closeImportExportPanelOpen } = usePeriodView();
  const {
    managementCompanies,
    isManagementCompanyLoading,
    fileDownloadLoading,
    setFileDownloadLoading,
  } = props;
  const [managementCompany, setManagementCompany] = useState<ManagementCompanyObject>(
    {} as ManagementCompanyObject
  );
  const [property, setProperty] = useState<PropertyObject>({} as PropertyObject);
  const [fiscalYear, setFiscalYear] = useState<fiscalYearType | null>(null);
  const [fileType, setFileType] = useState<'xls' | 'txt'>('xls');
  const [tab, setTab] = useState<'multi' | 'single'>('multi');
  const [glCodeType, setGlCodeType] = useState<'active' | 'all'>('active');
  const [amountType, setAmountType] = useState<'forecast' | 'budget'>('forecast');
  const isManagementCompanyEmpty = useMemo(
    () => isSuperAdmin && isEmpty(managementCompany),
    [isSuperAdmin, managementCompany]
  );

  const allProperties = property.companyName === 'All';
  const { fiscalYears, getFiscalYears, isLoading } = useAllGetFiscalYearsApi(
    isSuperAdmin ? managementCompany?.companyId : undefined
  );

  useEffect(() => {
    if (isSuperAdmin) {
      if (managementCompany && managementCompany.companyId) {
        getFiscalYears();
      }
    } else {
      getFiscalYears();
    }
  }, [managementCompany, getFiscalYears, isSuperAdmin]);

  const fiscalYearDropdownData = useMemo(
    () =>
      formatFiscalYears(
        fiscalYears.fiscalYears?.filter((item, index, self) => {
          if (allProperties) {
            const dateKey = `${item.startDate}-${item.endDate}`;
            return index === self.findIndex((obj) => `${obj.startDate}-${obj.endDate}` === dateKey);
          }
          return item?.companyId === property.companyId;
        }) || []
      ),
    [allProperties, fiscalYears, property.companyId]
  );

  const properties: PropertyObject[] = useMemo(() => {
    const properties = getUniqueProperties(fiscalYears.fiscalYears);
    if (!properties.length) {
      return [];
    }
    if (properties.length === 1) {
      return properties;
    }
    return [
      {
        companyName: 'All',
        companyId: 0,
        totalGlCount: sumBy(properties, 'totalGlCount'),
        activeGlCount: sumBy(properties, 'activeGlCount'),
        usesInitialBudgets: properties.some((prop) => prop.usesInitialBudgets),
      } as PropertyObject,
      ...properties,
    ];
  }, [fiscalYears]);

  const isExcelRowExceed = useMemo(
    () =>
      glCodeType === 'active'
        ? property.activeGlCount > MAX_EXCEL_ROWS
        : property.totalGlCount > MAX_EXCEL_ROWS,
    [glCodeType, property.activeGlCount, property.totalGlCount]
  );

  useEffect(() => {
    if (properties.length) {
      if (!property?.companyId) {
        setProperty(properties[0]);
      }
      setFileType('xls');
    }
  }, [fiscalYears, properties, property?.companyId]);

  useEffect(() => {
    if (managementCompanies.length === 1) {
      setManagementCompany(managementCompanies[0]);
    }
  }, [managementCompanies]);

  useEffect(() => {
    if (fileType === 'xls' && isExcelRowExceed) setFileType('txt');
  }, [fileType, isExcelRowExceed]);

  const handleFileTypeChange = (event: RadioButtonChangeEvent) => {
    const { value } = event;
    setFileType(value);
  };
  const handleTabChange = (event: RadioGroupChangeEvent) => {
    const { value } = event;
    setTab(value);
  };
  const handleGlCodeTypeChange = (event: RadioGroupChangeEvent) => {
    const { value } = event;
    setGlCodeType(value);
  };
  const handleAmountTypeChange = (event: RadioGroupChangeEvent) => {
    const { value } = event;
    setAmountType(value);
  };

  const { exportExecute, exportLoading } = useExportGLBudgetsApi();

  const amountTypeConverter = () => {
    if (property.usesInitialBudgets) {
      if (amountType.includes('budget')) return 0;
      else return 1;
    }
    return 1;
  };

  const { confirm, onClose } = useConfirm();

  const navigate = useNavigate();

  const [isWaiting, setIsWaiting] = useState(false);

  const { getUrl, redirect } = useRedirect();

  useEffect(() => {
    const fileDownloadDelayChecking = async () => {
      if (fileDownloadLoading && isWaiting) {
        const isNewTab = await confirm({
          title: 'Your file will be downloaded shortly',
          content: (
            <h1>
              Do you wish to continue using the application while the file is being processed?
            </h1>
          ),
          cancelButtonText: 'Stay in the current tab',
          confirmButtonText: 'Open a duplicate tab',
        });
        const url = getUrl('checkbook');
        if (isNewTab) redirect(url, false, true);
        setIsWaiting(false);
      } else {
        onClose();
        setIsWaiting(false);
      }
    };
    fileDownloadDelayChecking();
  }, [confirm, fileDownloadLoading, isWaiting, navigate, onClose, redirect, getUrl]);

  const handleExport = async () => {
    setFileDownloadLoading(true);
    setTimeout(() => setIsWaiting(true), 3000);
    const exportParams = {
      allProperties: allProperties,
      ExportCompanyIds: allProperties
        ? properties.map((p) => p.companyId).filter((i) => !!i)
        : [property.companyId],
      fileType: fileType === 'xls' ? 0 : 1,
      tabs: tab === 'single' ? 0 : 1,
      startDate: fiscalYear?.startDate as string,
      endDate: fiscalYear?.endDate as string,
      exportType: glCodeType === 'active' ? 0 : 1,
      amountType: amountTypeConverter(),
    };
    const { data, status } = await exportExecute(exportParams);
    if (!data || (!data?.downloadUrl && status === 202)) {
      setFileDownloadLoading(false);
      toast.success('Email Request Received.');
      return;
    }

    const response = await fetch(data.downloadUrl);

    const isExcel = fileType === 'xls';
    const fileBlob = isExcel
      ? await response.blob()
      : new Blob([await response.text()], { type: 'text/plain' });
    const fileLink = document.createElement('a');
    fileLink.href = URL.createObjectURL(fileBlob);
    fileLink.download = data.fileName;
    document.body.appendChild(fileLink);
    fileLink.click();
    document.body.removeChild(fileLink);
    URL.revokeObjectURL(fileLink.href);
    setFileDownloadLoading(false);
    toast.success('File Exported successfully.');
  };

  const { errors, isExportDisabled } = useMemo(() => {
    const errors: string[] = [];
    const isExportDisabled: boolean =
      (isSuperAdmin && !Object.values(managementCompany).length) ||
      !Object.values(property).length ||
      !fiscalYear ||
      (property.usesInitialBudgets && !amountType.length);

    if (isSuperAdmin && !Object.values(managementCompany).length) {
      errors.push('Select Management Company to Export.');
    }
    if (!Object.values(property).length) {
      errors.push('Select Property to Export.');
    }
    if (!fiscalYear) {
      errors.push('Select Fiscal Year to Export.');
    }
    if (property.usesInitialBudgets && !amountType.length) {
      errors.push('Select Export Amounts');
    }

    return { errors, isExportDisabled };
  }, [amountType.length, fiscalYear, isSuperAdmin, managementCompany, property]);

  const sortedManagementCompanies = useMemo(
    () => managementCompanies?.sort((a, b) => a.companyName.localeCompare(b.companyName)),
    [managementCompanies]
  );

  const sortedProperties = useMemo(
    () =>
      properties?.sort((a, b) => {
        if (a.companyName === 'All') return -1;
        if (b.companyName === 'All') return 1;
        return a.companyName.localeCompare(b.companyName);
      }),
    [properties]
  );

  return (
    <div className="p-[10px] bg-[#ffffff]">
      <p className="text-xs mb-2 mt-2">
        {isSuperAdmin ? (
          <span>
            Note: Here you can export the Checkbook's GL Code related data. Select the management
            company and corresponding property you'd like to export. You can select all properties,
            and then in the export you can decide if you want each property displayed in it's own
            tab, or only active codes be included.
          </span>
        ) : (
          <span>
            Note: Here you can export the Checkbook's GL Code related data. Select the property
            you'd like to export. You can select all properties, and then in the export you can
            decide if you want each property displayed in it's own tab, or only active codes be
            included.
          </span>
        )}
      </p>
      {isSuperAdmin && (
        <ManagementCompanyDropdown
          data={sortedManagementCompanies}
          managementCompany={managementCompany}
          setManagementCompany={setManagementCompany}
          loading={isManagementCompanyLoading}
          setProperty={setProperty}
          properties={properties}
        />
      )}

      <PropertyDropdown
        propertyList={sortedProperties}
        property={property}
        setProperty={setProperty}
        disabled={isManagementCompanyEmpty}
        loading={isLoading}
      />
      {!isManagementCompanyEmpty && (
        <>
          <div className="mb-2">
            <p className="font-bold text-xs">File Type</p>
            <div className="mt-1">
              {fileTypeData.map((file) => (
                <span className="text-xs flex items-center mb-2" key={file.value}>
                  <RadioButton
                    value={file.value}
                    label={file.label}
                    checked={file.value === fileType}
                    onChange={handleFileTypeChange}
                    disabled={file.value === 'xls' && isExcelRowExceed}
                  />
                  {file.value === 'xls' && isExcelRowExceed && (
                    <PopUp
                      popupAlign={{ horizontal: 'center', vertical: 'top' }}
                      content={
                        <PopUpContentWrapper
                          type="Error"
                          contents={['Exported file would exceed row limit in excel']}
                        />
                      }
                    >
                      <span className="ml-2 flex items-center">{errorIcon}</span>
                    </PopUp>
                  )}
                </span>
              ))}
            </div>
          </div>
          {allProperties && fileType === 'xls' && (
            <div className="mb-2">
              <label className="font-bold text-xs">Tabs</label>
              <RadioGroup
                data={tabData}
                layout={'vertical'}
                className="text-xs"
                onChange={handleTabChange}
                value={tab}
              />
            </div>
          )}
        </>
      )}

      <FiscalYearDropdown
        fiscalYear={fiscalYear}
        setFiscalYear={setFiscalYear}
        disabled={isManagementCompanyEmpty}
        data={fiscalYearDropdownData}
        loading={isLoading}
      />

      <div className="mb-2">
        <label className="font-bold text-xs">Export GL Codes</label>
        <RadioGroup
          data={glCodeTypeData}
          layout={'horizontal'}
          className="text-xs"
          onChange={handleGlCodeTypeChange}
          value={glCodeType}
        />
      </div>
      {property.usesInitialBudgets && (
        <div className="mb-2">
          <label className="font-bold text-xs mb-2 block">Export Amounts</label>
          <RadioGroup
            data={amountTypeData}
            layout={'horizontal'}
            className="text-xs"
            onChange={handleAmountTypeChange}
            value={amountType}
          />
        </div>
      )}
      <div className="flex items-center justify-end">
        <PopUp
          show={isExportDisabled}
          content={<PopUpContentWrapper type="Error" contents={errors} />}
        >
          <Button
            size={'md'}
            color={'info'}
            loading={exportLoading}
            onClick={handleExport}
            disabled={isExportDisabled}
          >
            {property.companyId !== 0 ? t('Export') : t('Email Download Link')}
          </Button>
        </PopUp>
        <Button
          size="md"
          color="secondary"
          onClick={closeImportExportPanelOpen}
          disabled={exportLoading || fileDownloadLoading}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default Export;
